/**
 * Push Notification Service
 * Handles browser push notifications for reminders
 */

const NOTIFICATION_PERMISSION_KEY = 'notificationPermission';
const NOTIFICATION_SETTINGS_KEY = 'notificationSettings';

// Default notification settings
const DEFAULT_SETTINGS = {
  enabled: false,
  documentExpiring: true,
  parkingExpiring: true,
  calendarEvents: true,
  importantDates: true,
  advanceMinutes: {
    documents: 1440, // 24 hours before
    parking: 15,     // 15 minutes before
    calendar: 30,    // 30 minutes before
    dates: 1440,     // 24 hours before
  }
};

/**
 * Check if browser supports notifications
 */
export const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

/**
 * Get current notification permission status
 */
export const getNotificationPermission = () => {
  if (!isNotificationSupported()) return 'unsupported';
  return Notification.permission;
};

/**
 * Request notification permission from user
 */
export const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    return { granted: false, reason: 'Notifications not supported in this browser' };
  }

  try {
    const permission = await Notification.requestPermission();
    localStorage.setItem(NOTIFICATION_PERMISSION_KEY, permission);
    
    if (permission === 'granted') {
      return { granted: true };
    } else if (permission === 'denied') {
      return { granted: false, reason: 'Permission denied. Please enable in browser settings.' };
    } else {
      return { granted: false, reason: 'Permission dismissed' };
    }
  } catch (error) {
    return { granted: false, reason: error.message };
  }
};

/**
 * Get notification settings from localStorage
 */
export const getNotificationSettings = () => {
  try {
    const stored = localStorage.getItem(NOTIFICATION_SETTINGS_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Failed to load notification settings:', error);
  }
  return DEFAULT_SETTINGS;
};

/**
 * Save notification settings to localStorage
 */
export const saveNotificationSettings = (settings) => {
  try {
    localStorage.setItem(NOTIFICATION_SETTINGS_KEY, JSON.stringify(settings));
    return true;
  } catch (error) {
    console.error('Failed to save notification settings:', error);
    return false;
  }
};

/**
 * Show a notification
 */
export const showNotification = (title, options = {}) => {
  if (getNotificationPermission() !== 'granted') {
    console.warn('Notification permission not granted');
    return null;
  }

  const defaultOptions = {
    icon: '/logo192.png',
    badge: '/logo192.png',
    vibrate: [200, 100, 200],
    requireInteraction: false,
    ...options,
  };

  try {
    const notification = new Notification(title, defaultOptions);
    
    notification.onclick = () => {
      window.focus();
      if (options.onClick) {
        options.onClick();
      }
      notification.close();
    };

    return notification;
  } catch (error) {
    console.error('Failed to show notification:', error);
    return null;
  }
};

/**
 * Schedule a notification for a future time
 */
export const scheduleNotification = (title, options = {}, triggerTime) => {
  const now = new Date().getTime();
  const delay = triggerTime.getTime() - now;

  if (delay <= 0) {
    // Time has already passed, show immediately
    return showNotification(title, options);
  }

  // Schedule for future
  const timeoutId = setTimeout(() => {
    showNotification(title, options);
  }, delay);

  return timeoutId;
};

/**
 * Notification types with their configurations
 * 
 * SECURITY: Notification bodies should NOT contain sensitive medical information.
 * Examples of what NOT to include:
 * - Medication names or dosages ("Take 50mg Lisinopril")
 * - Specific medical conditions ("Diabetes checkup")
 * - Doctor names with specialties ("Dr. Smith (Oncologist)")
 * - Insurance policy numbers
 * - SSN or other identifiers
 * 
 * Instead use generic messages:
 * - "Time for your medication"
 * - "Upcoming medical appointment"
 * - "Document renewal needed"
 */
export const NOTIFICATION_TYPES = {
  DOCUMENT_EXPIRING: {
    type: 'document_expiring',
    // SECURITY: Shows document title only, not contents
    title: 'Document Expiring Soon',
    getBody: (doc) => `${doc.title} expires on ${new Date(doc.expiration_date).toLocaleDateString()}`,
    getTag: (doc) => `doc-${doc.id}`,
    icon: '📄',
  },
  PARKING_EXPIRING: {
    type: 'parking_expiring',
    title: 'Parking Expiring Soon',
    getBody: (parking) => `Your parking at ${parking.title} expires in ${parking.minutesLeft} minutes`,
    getTag: (parking) => `parking-${parking.id}`,
    icon: '🅿️',
  },
  CALENDAR_EVENT: {
    type: 'calendar_event',
    // SECURITY: Shows event title only, not medical details
    title: 'Upcoming Event',
    getBody: (event) => `${event.title} starts at ${new Date(event.start_date).toLocaleTimeString()}`,
    getTag: (event) => `event-${event.id}`,
    icon: '📅',
  },
  IMPORTANT_DATE: {
    type: 'important_date',
    title: 'Important Date Reminder',
    getBody: (date) => `${date.title} is on ${new Date(date.date).toLocaleDateString()}`,
    getTag: (date) => `date-${date.id}`,
    icon: '⭐',
  },
  MEDICATION_REMINDER: {
    type: 'medication_reminder',
    // SECURITY: Generic message - does NOT include medication name or dosage
    title: 'Medication Reminder',
    getBody: () => 'Time for your scheduled medication',
    getTag: (med) => `med-${med.id}`,
    icon: '💊',
  },
  APPOINTMENT_REMINDER: {
    type: 'appointment_reminder',
    // SECURITY: Generic message - does NOT include doctor name or specialty
    title: 'Appointment Reminder',
    getBody: (apt) => `Upcoming appointment at ${new Date(apt.date).toLocaleTimeString()}`,
    getTag: (apt) => `apt-${apt.id}`,
    icon: '🏥',
  },
};

/**
 * Check and trigger notifications for upcoming reminders
 */
export const checkAndNotify = async (items, type, settings) => {
  if (!settings.enabled || getNotificationPermission() !== 'granted') {
    return;
  }

  const notificationType = NOTIFICATION_TYPES[type];
  if (!notificationType) return;

  const now = new Date();
  
  for (const item of items) {
    const triggerTime = getTriggerTime(item, type, settings);
    if (!triggerTime) continue;

    const timeDiff = triggerTime.getTime() - now.getTime();
    
    // Only notify if within the advance window and not past
    if (timeDiff > 0 && timeDiff <= settings.advanceMinutes[type.toLowerCase()] * 60 * 1000) {
      showNotification(notificationType.title, {
        body: notificationType.getBody(item),
        tag: notificationType.getTag(item),
        data: { type: type, itemId: item.id },
      });
    }
  }
};

/**
 * Get the trigger time for a notification based on item type
 */
const getTriggerTime = (item, type, settings) => {
  let targetDate;
  
  switch (type) {
    case 'DOCUMENT_EXPIRING':
      if (!item.expiration_date) return null;
      targetDate = new Date(item.expiration_date);
      break;
    case 'PARKING_EXPIRING':
      if (!item.paid_until && !item.leave_by) return null;
      targetDate = new Date(item.paid_until || item.leave_by);
      break;
    case 'CALENDAR_EVENT':
      if (!item.start_date) return null;
      targetDate = new Date(item.start_date);
      break;
    case 'IMPORTANT_DATE':
      if (!item.date) return null;
      targetDate = new Date(item.date);
      break;
    default:
      return null;
  }

  // Calculate notification time (advance notice)
  const advanceMs = (settings.advanceMinutes[type.toLowerCase().replace('_expiring', '').replace('_event', '')] || 30) * 60 * 1000;
  return new Date(targetDate.getTime() - advanceMs);
};

export default {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  getNotificationSettings,
  saveNotificationSettings,
  showNotification,
  scheduleNotification,
  checkAndNotify,
  NOTIFICATION_TYPES,
};
